import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import './Dropdown.css';

/**
 * Custom dropdown component that replaces native <select> / Form.Select.
 *
 * @param {Object[]} options           – Array of {value, label, disabled?} objects.
 * @param {string}   value             – Currently selected value (controlled).
 * @param {function} onChange           – Called with the new value string when the user picks an option.
 * @param {string}   [placeholder]     – Text shown when nothing is selected.
 * @param {string}   [name]            – Optional name, forwarded via a synthetic-like event to onChange.
 * @param {boolean}  [disabled=false]  – Disable the dropdown.
 * @param {string}   [size]            – "sm" for a compact variant.
 * @param {string}   [className]       – Extra class names on the root element.
 * @param {Object}   [style]           – Inline styles on the root element.
 * @param {boolean}  [block=false]     – If true the dropdown spans the full width of its parent.
 */
const Dropdown = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  name,
  disabled = false,
  size,
  className = '',
  style,
  block = false,
}) => {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  // ── Derived state ────────────────────────────────────────────────────────
  const selectedOption = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value],
  );

  // ── Close on outside click ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // ── Scroll focused option into view ──────────────────────────────────────
  useEffect(() => {
    if (!open || focusedIndex < 0 || !menuRef.current) return;
    const items = menuRef.current.querySelectorAll('.scc-dropdown__option');
    if (items[focusedIndex]) {
      items[focusedIndex].scrollIntoView({block: 'nearest'});
    }
  }, [focusedIndex, open]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleToggle = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => {
      if (!prev) {
        // When opening, focus the currently selected option
        const idx = options.findIndex((o) => String(o.value) === String(value));
        setFocusedIndex(idx >= 0 ? idx : 0);
      }
      return !prev;
    });
  }, [disabled, options, value]);

  const handleSelect = useCallback(
    (optionValue) => {
      if (disabled) return;
      setOpen(false);

      // Mimic native event shape so existing handlers like e.target.value / e.target.name work
      if (name) {
        onChange({target: {value: optionValue, name}});
      } else {
        onChange(optionValue);
      }
    },
    [disabled, onChange, name],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (open && focusedIndex >= 0 && !options[focusedIndex]?.disabled) {
            handleSelect(options[focusedIndex].value);
          } else {
            handleToggle();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!open) {
            handleToggle();
          } else {
            setFocusedIndex((prev) => {
              let next = prev + 1;
              while (next < options.length && options[next]?.disabled) next++;
              return next < options.length ? next : prev;
            });
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (open) {
            setFocusedIndex((prev) => {
              let next = prev - 1;
              while (next >= 0 && options[next]?.disabled) next--;
              return next >= 0 ? next : prev;
            });
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
        case 'Tab':
          setOpen(false);
          break;
        default:
          break;
      }
    },
    [disabled, open, focusedIndex, options, handleSelect, handleToggle],
  );

  // ── Root class names ─────────────────────────────────────────────────────
  const rootClasses = [
    'scc-dropdown',
    open && 'scc-dropdown--open',
    size === 'sm' && 'scc-dropdown--sm',
    block && 'scc-dropdown--block',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const triggerClasses = [
    'scc-dropdown__trigger',
    disabled && 'scc-dropdown__trigger--disabled',
    !selectedOption && 'scc-dropdown__trigger--placeholder',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={rootRef} className={rootClasses} style={style}>
      <button
        type="button"
        className={triggerClasses}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        tabIndex={0}
      >
        <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
      </button>
      <span className="scc-dropdown__arrow" />

      {open && (
        <div ref={menuRef} className="scc-dropdown__menu" role="listbox">
          {options.map((opt, idx) => {
            const isSelected = String(opt.value) === String(value);
            const isFocused = idx === focusedIndex;
            const optClasses = [
              'scc-dropdown__option',
              isSelected && 'scc-dropdown__option--selected',
              isFocused && 'scc-dropdown__option--focused',
              opt.disabled && 'scc-dropdown__option--disabled',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                type="button"
                key={opt.value}
                className={optClasses}
                role="option"
                aria-selected={isSelected}
                onClick={() => !opt.disabled && handleSelect(opt.value)}
                onMouseEnter={() => setFocusedIndex(idx)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  block: PropTypes.bool,
};

export default Dropdown;


